// SyncEngine over IPC, for Electron (D-019): the database and sync run in the main process
// (@powersync/node, better-sqlite3), the station UI runs in the renderer. Both halves live
// here so the protocol has one definition:
//
//   renderer: `new IpcSyncEngine(window.stationHost.sync)` — a SyncEngine like any other;
//   main:     `serveSyncEngine(engine, channel)` — answers the renderer from a real engine
//             (a PowerSyncEngine over @powersync/node; WP3.4 wires ipcMain/webContents).
//
// The connector (credentials, `uploadData`) stays in the renderer with the rest of the
// station logic: when the main engine needs it, it sends a `connector` event and the
// renderer answers with `connector.result`. `uploadData` then reads batches over IPC.
//
// Write transactions are sessions: `tx.begin` opens one in main and holds the write lock
// until `tx.commit`/`tx.rollback` (or a timeout, if the renderer goes away).
// Everything crossing is plain data (structured-clone safe; instants as ISO strings).

import type { CommandEnvelope } from '@enp/domain';
import {
  type CommandDraft,
  type CommandDraftInTx,
  type ConnectOptions,
  INITIAL_ENGINE_STATUS,
  type LocalEffect,
  type LocalTx,
  type SqlValue,
  type StreamSubscription,
  type SyncConnector,
  type SyncEngine,
  type SyncEngineStatus,
  type UploadBatch,
  type UploadOp,
} from './engine.js';
import { enqueueCommand } from './outbox.js';
import type { SqlAccess } from './sql-guard.js';
import { Store, errorMessage } from './util.js';

/** Every request the renderer sends. */
export const SYNC_IPC_METHODS = [
  'open',
  'close',
  'connect',
  'disconnect',
  'status',
  'getAll',
  'getOptional',
  'watch.start',
  'watch.stop',
  'tx.begin',
  'tx.getAll',
  'tx.getOptional',
  'tx.execute',
  'tx.commit',
  'tx.rollback',
  'upload.next',
  'upload.complete',
  'upload.count',
  'uploadNow',
  'clearLocalData',
  'streams.set',
  'poke',
  'connector.result',
] as const;
export type SyncIpcMethod = (typeof SYNC_IPC_METHODS)[number];

/** `SyncEngineStatus` as it crosses (the date as ISO). */
export type WireStatus = Omit<SyncEngineStatus, 'lastSyncedAt'> & { lastSyncedAt: string | null };

export type SyncIpcEvent =
  | { type: 'status'; status: WireStatus }
  | { type: 'watch'; watchId: string; rows?: unknown[]; error?: string }
  | { type: 'connector'; callId: string; method: 'fetchCredentials' | 'uploadData' };

/** Renderer end (the preload exposes it as `window.stationHost.sync`). */
export interface SyncIpcChannel {
  invoke(method: SyncIpcMethod, ...args: unknown[]): Promise<unknown>;
  onEvent(listener: (event: SyncIpcEvent) => void): () => void;
}

/** Main end (ipcMain.handle + webContents.send, WP3.4). */
export interface SyncIpcServerChannel {
  handle(method: SyncIpcMethod, handler: (...args: unknown[]) => Promise<unknown>): void;
  emit(event: SyncIpcEvent): void;
}

const toWire = (s: SyncEngineStatus): WireStatus => ({
  ...s,
  lastSyncedAt: s.lastSyncedAt ? s.lastSyncedAt.toISOString() : null,
});
const fromWire = (s: WireStatus): SyncEngineStatus => ({
  ...s,
  lastSyncedAt: s.lastSyncedAt ? new Date(s.lastSyncedAt) : undefined,
});

/** Stream subscriptions from the renderer, checked: names and parameters are plain strings. */
function streamsFromWire(value: unknown): StreamSubscription[] {
  if (!Array.isArray(value)) throw new Error('Stream subscriptions must be a list');
  return value.map((v: unknown) => {
    const s = v as { stream?: unknown; params?: unknown };
    if (typeof s?.stream !== 'string' || typeof s.params !== 'object' || s.params === null) {
      throw new Error('A stream subscription needs a stream name and parameters');
    }
    const params: Record<string, string> = {};
    for (const [k, p] of Object.entries(s.params)) {
      if (typeof p !== 'string') throw new Error('Stream parameters must be text');
      params[k] = p;
    }
    return { stream: s.stream, params };
  });
}

// ---------------------------------------------------------------------------------------
// Renderer

export class IpcSyncEngine implements SyncEngine {
  readonly kind = 'electron-ipc' as const;
  readonly #status = new Store<SyncEngineStatus>({ ...INITIAL_ENGINE_STATUS });
  readonly #watchers = new Map<
    string,
    { onResult: (rows: never[]) => void; onError?: (e: Error) => void }
  >();
  #connector: SyncConnector | undefined;
  #nextId = 0;
  readonly #stopEvents: () => void;

  constructor(private readonly channel: SyncIpcChannel) {
    this.#stopEvents = channel.onEvent((event) => void this.#onEvent(event));
  }

  async open(): Promise<void> {
    await this.channel.invoke('open');
    this.#status.set(fromWire((await this.channel.invoke('status')) as WireStatus));
  }

  async close(): Promise<void> {
    await this.channel.invoke('close');
    this.#stopEvents();
  }

  async connect(connector: SyncConnector, options: ConnectOptions = {}): Promise<void> {
    this.#connector = connector;
    await this.channel.invoke('connect', options);
  }

  async disconnect(): Promise<void> {
    await this.channel.invoke('disconnect');
    this.#connector = undefined;
  }

  async setStreamSubscriptions(subscriptions: readonly StreamSubscription[]): Promise<void> {
    await this.channel.invoke(
      'streams.set',
      subscriptions.map((s) => ({ stream: s.stream, params: { ...s.params } })),
    );
  }

  getStatus(): SyncEngineStatus {
    return this.#status.get();
  }

  subscribe(listener: (status: SyncEngineStatus) => void): () => void {
    return this.#status.subscribe(listener);
  }

  async getAll<T>(sql: string, params: readonly SqlValue[] = []): Promise<T[]> {
    return (await this.channel.invoke('getAll', sql, [...params])) as T[];
  }

  async getOptional<T>(sql: string, params: readonly SqlValue[] = []): Promise<T | null> {
    return (await this.channel.invoke('getOptional', sql, [...params])) as T | null;
  }

  watch<T>(
    sql: string,
    params: readonly SqlValue[],
    onResult: (rows: T[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const watchId = `w${++this.#nextId}`;
    this.#watchers.set(watchId, { onResult: onResult as (rows: never[]) => void, onError });
    this.channel.invoke('watch.start', watchId, sql, [...params]).catch((err: unknown) => {
      onError?.(new Error(errorMessage(err)));
    });
    return () => {
      this.#watchers.delete(watchId);
      void this.channel.invoke('watch.stop', watchId).catch(() => undefined);
    };
  }

  async writeTransaction<T>(fn: (tx: LocalTx) => Promise<T>): Promise<T> {
    const txId = (await this.channel.invoke('tx.begin')) as string;
    const tx: LocalTx = {
      getAll: async <R>(sql: string, params: readonly SqlValue[] = []) =>
        (await this.channel.invoke('tx.getAll', txId, sql, [...params])) as R[],
      getOptional: async <R>(sql: string, params: readonly SqlValue[] = []) =>
        (await this.channel.invoke('tx.getOptional', txId, sql, [...params])) as R | null,
      execute: async (sql: string, params: readonly SqlValue[] = []) =>
        (await this.channel.invoke('tx.execute', txId, sql, [...params])) as {
          rowsAffected: number;
        },
    };
    let result: T;
    try {
      result = await fn(tx);
    } catch (err) {
      await this.channel.invoke('tx.rollback', txId).catch(() => undefined);
      throw err;
    }
    await this.channel.invoke('tx.commit', txId);
    return result;
  }

  async enqueueCommand(
    draft: CommandDraft | CommandDraftInTx,
    localEffect?: LocalEffect,
  ): Promise<CommandEnvelope> {
    const envelope = await enqueueCommand(this, draft, localEffect);
    void this.channel.invoke('poke').catch(() => undefined);
    return envelope;
  }

  async nextUploadBatch(limit: number): Promise<UploadBatch | null> {
    const res = (await this.channel.invoke('upload.next', limit)) as {
      batchId: string;
      ops: UploadOp[];
      haveMore: boolean;
    } | null;
    if (!res) return null;
    return {
      ops: res.ops,
      haveMore: res.haveMore,
      complete: async () => {
        await this.channel.invoke('upload.complete', res.batchId);
      },
    };
  }

  async queuedCommandCount(): Promise<number> {
    return Number(await this.channel.invoke('upload.count'));
  }

  async uploadNow(): Promise<void> {
    await this.channel.invoke('uploadNow');
  }

  async clearLocalData(): Promise<void> {
    await this.channel.invoke('clearLocalData');
    this.#connector = undefined;
  }

  async #onEvent(event: SyncIpcEvent) {
    switch (event.type) {
      case 'status':
        this.#status.set(fromWire(event.status));
        return;
      case 'watch': {
        const w = this.#watchers.get(event.watchId);
        if (!w) return;
        if (event.error !== undefined) w.onError?.(new Error(event.error));
        else w.onResult((event.rows ?? []) as never[]);
        return;
      }
      case 'connector': {
        const connector = this.#connector;
        try {
          if (!connector) throw new Error('No connector in the renderer');
          const value =
            event.method === 'fetchCredentials'
              ? await connector
                  .fetchCredentials()
                  .then((c) => (c ? { ...c, expiresAt: c.expiresAt?.toISOString() ?? null } : null))
              : await connector.uploadData(this).then(() => null);
          await this.channel.invoke('connector.result', event.callId, { ok: true, value });
        } catch (err) {
          // "No answer from the server" must survive the trip: the station says "can't reach
          // the server" for it, not "sync failed".
          const network = (err as { isNetwork?: unknown } | null)?.isNetwork === true;
          await this.channel
            .invoke('connector.result', event.callId, {
              ok: false,
              error: errorMessage(err),
              ...(network ? { network } : {}),
            })
            .catch(() => undefined);
        }
        return;
      }
    }
  }
}

// ---------------------------------------------------------------------------------------
// Main process

export interface ServeOptions {
  /** A transaction the renderer leaves open longer than this is rolled back. Default 30 s. */
  txTimeoutMs?: number;
  /**
   * Checks every statement from the renderer before it runs, and throws to refuse it (the
   * Electron main process passes `checkPageSql`, review CR8B-1). Default: no check.
   */
  checkSql?: (sql: string, access: SqlAccess) => void;
}

class RollbackSignal extends Error {}

/** A connector call that failed in the renderer, as the main engine sees it. */
class ConnectorCallError extends Error {
  constructor(
    message: string,
    /** No answer from the server (ApiError's `isNetwork`, carried over IPC). */
    readonly isNetwork: boolean,
  ) {
    super(message);
    this.name = 'ConnectorCallError';
  }
}

interface TxSession {
  tx: LocalTx | undefined;
  commit: () => void;
  rollback: (error: Error) => void;
  /** Settles when the transaction has committed or rolled back. */
  done: Promise<unknown>;
  timer: ReturnType<typeof setTimeout> | undefined;
}

/** Answers an {@link IpcSyncEngine} from `engine`. Returns a function that stops serving. */
export function serveSyncEngine(
  engine: SyncEngine,
  channel: SyncIpcServerChannel,
  options: ServeOptions = {},
): () => void {
  const txTimeoutMs = options.txTimeoutMs ?? 30_000;
  const checkSql = options.checkSql ?? (() => undefined);
  /** The statement, once `checkSql` has allowed it for `access`. */
  const checked = (sql: unknown, access: SqlAccess): string => {
    const text = String(sql);
    checkSql(text, access);
    return text;
  };
  const watches = new Map<string, () => void>();
  const batches = new Map<string, UploadBatch>();
  const pendingCalls = new Map<
    string,
    { resolve: (v: unknown) => void; reject: (e: Error) => void }
  >();
  const txs = new Map<string, TxSession>();
  let nextId = 0;
  const id = (p: string) => `${p}${++nextId}`;

  const stopStatus = engine.subscribe((s) => channel.emit({ type: 'status', status: toWire(s) }));

  const callRenderer = (method: 'fetchCredentials' | 'uploadData') =>
    new Promise<unknown>((resolve, reject) => {
      const callId = id('c');
      pendingCalls.set(callId, { resolve, reject });
      channel.emit({ type: 'connector', callId, method });
    });

  const proxyConnector: SyncConnector = {
    fetchCredentials: async () => {
      const c = (await callRenderer('fetchCredentials')) as {
        endpoint: string;
        token: string;
        expiresAt: string | null;
      } | null;
      return c
        ? {
            endpoint: c.endpoint,
            token: c.token,
            ...(c.expiresAt ? { expiresAt: new Date(c.expiresAt) } : {}),
          }
        : null;
    },
    uploadData: async () => {
      await callRenderer('uploadData');
    },
  };

  const txOf = (txId: unknown) => {
    const t = txs.get(String(txId));
    if (!t?.tx) throw new Error('That transaction is no longer open');
    return { ...t, tx: t.tx };
  };

  const handlers: Record<SyncIpcMethod, (...args: unknown[]) => Promise<unknown>> = {
    open: () => engine.open(),
    close: () => engine.close(),
    connect: (opts) => engine.connect(proxyConnector, (opts ?? {}) as ConnectOptions),
    disconnect: () => engine.disconnect(),
    status: async () => toWire(engine.getStatus()),
    getAll: async (sql, params) =>
      engine.getAll(checked(sql, 'read'), (params ?? []) as SqlValue[]),
    getOptional: async (sql, params) =>
      engine.getOptional(checked(sql, 'read'), (params ?? []) as SqlValue[]),
    'watch.start': async (watchId, sql, params) => {
      const wid = String(watchId);
      const statement = checked(sql, 'read');
      watches.get(wid)?.();
      watches.set(
        wid,
        engine.watch(
          statement,
          (params ?? []) as SqlValue[],
          (rows) => channel.emit({ type: 'watch', watchId: wid, rows }),
          (err) => channel.emit({ type: 'watch', watchId: wid, error: err.message }),
        ),
      );
    },
    'watch.stop': async (watchId) => {
      watches.get(String(watchId))?.();
      watches.delete(String(watchId));
    },
    'tx.begin': () =>
      new Promise<string>((resolveBegin, rejectBegin) => {
        const txId = id('t');
        let began = false;
        const entry: TxSession = {
          tx: undefined,
          commit: () => undefined,
          rollback: () => undefined,
          done: Promise.resolve(),
          timer: undefined,
        };
        entry.done = engine
          .writeTransaction(
            (tx) =>
              new Promise<void>((commit, rollback) => {
                began = true;
                entry.tx = tx;
                entry.commit = () => commit();
                entry.rollback = rollback;
                entry.timer = setTimeout(
                  () => rollback(new RollbackSignal('Transaction timed out')),
                  txTimeoutMs,
                );
                txs.set(txId, entry);
                resolveBegin(txId);
              }),
          )
          .finally(() => {
            clearTimeout(entry.timer);
            txs.delete(txId);
          });
        entry.done.catch((err: unknown) => {
          if (!began) rejectBegin(err);
        });
      }),
    'tx.getAll': async (txId, sql, params) =>
      txOf(txId).tx.getAll(checked(sql, 'write'), (params ?? []) as SqlValue[]),
    'tx.getOptional': async (txId, sql, params) =>
      txOf(txId).tx.getOptional(checked(sql, 'write'), (params ?? []) as SqlValue[]),
    'tx.execute': async (txId, sql, params) =>
      txOf(txId).tx.execute(checked(sql, 'write'), (params ?? []) as SqlValue[]),
    'tx.commit': async (txId) => {
      const t = txOf(txId);
      t.commit();
      await t.done;
    },
    'tx.rollback': async (txId) => {
      const t = txs.get(String(txId));
      if (!t) return;
      t.rollback(new RollbackSignal('Rolled back'));
      await t.done.catch(() => undefined);
    },
    'upload.next': async (limit) => {
      const batch = await engine.nextUploadBatch(Number(limit));
      if (!batch) return null;
      const batchId = id('b');
      // Only the latest batch can still be completed: the renderer reads one at a time, and
      // an older one it never completed must not stay here forever (review CR8B-14).
      batches.clear();
      batches.set(batchId, batch);
      return { batchId, ops: batch.ops, haveMore: batch.haveMore };
    },
    'upload.complete': async (batchId) => {
      const batch = batches.get(String(batchId));
      if (!batch) throw new Error('Unknown upload batch');
      batches.delete(String(batchId));
      await batch.complete();
    },
    'upload.count': () => engine.queuedCommandCount(),
    uploadNow: () => engine.uploadNow(),
    clearLocalData: () => engine.clearLocalData(),
    'streams.set': (subscriptions) => engine.setStreamSubscriptions(streamsFromWire(subscriptions)),
    poke: async () => {
      void engine.uploadNow();
    },
    'connector.result': async (callId, result) => {
      const call = pendingCalls.get(String(callId));
      if (!call) return;
      pendingCalls.delete(String(callId));
      const r = result as { ok: boolean; value?: unknown; error?: string; network?: boolean };
      if (r.ok) call.resolve(r.value);
      else
        call.reject(new ConnectorCallError(r.error ?? 'Connector call failed', r.network === true));
    },
  };

  for (const method of SYNC_IPC_METHODS) channel.handle(method, handlers[method]);

  return () => {
    stopStatus();
    for (const stop of watches.values()) stop();
    watches.clear();
    for (const t of txs.values()) t.rollback(new RollbackSignal('Stopped'));
    for (const c of pendingCalls.values()) c.reject(new Error('Stopped'));
    pendingCalls.clear();
  };
}
